import React, { useState, useEffect } from 'react';
import api from '../../api/client';
import {
  Shield, FileText, AlertTriangle, Eye, Clock,
  RefreshCw
} from 'lucide-react';

const eventTypeLabels = {
  PHI_ACCESS: 'PHI Access',
  PHI_CREATE: 'PHI Create',
  PHI_UPDATE: 'PHI Update',
  PHI_DELETE: 'PHI Delete',
  AUTH_LOGIN: 'Login',
  AUTH_LOGOUT: 'Logout',
  AUTH_FAILED: 'Failed Login',
  EXPORT: 'Data Export',
  PRINT: 'Print',
  SHARE: 'Share',
  SYSTEM_ERROR: 'System Error',
  SYSTEM_CONFIG_CHANGE: 'Config Change',
  USER_ROLE_CHANGE: 'Role Change',
  INTEGRATION_ACCESS: 'Integration Access'
};

const eventTypeColors = {
  PHI_ACCESS: 'bg-blue-100 text-blue-700',
  PHI_CREATE: 'bg-emerald-100 text-emerald-700',
  PHI_UPDATE: 'bg-amber-100 text-amber-700',
  PHI_DELETE: 'bg-red-100 text-red-700',
  AUTH_LOGIN: 'bg-teal-100 text-teal-700',
  AUTH_LOGOUT: 'bg-slate-100 text-slate-600',
  AUTH_FAILED: 'bg-red-100 text-red-700',
  EXPORT: 'bg-purple-100 text-purple-700',
  SYSTEM_CONFIG_CHANGE: 'bg-orange-100 text-orange-700'
};

const ComplianceAudit = () => {
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [logsLoading, setLogsLoading] = useState(false);
  const [filter, setFilter] = useState('ALL');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => { loadStats(); }, []);
  useEffect(() => { loadLogs(); }, [filter, page]);

  const loadStats = async () => {
    try {
      const res = await api.get('/admin/settings/audit/stats');
      if (res.data?.success) setStats(res.data.data);
    } catch (err) {
      console.error('Failed to load audit stats:', err);
    }
  };

  const loadLogs = async () => {
    try {
      setLogsLoading(true);
      const params = new URLSearchParams({ page, limit: 20 });
      if (filter !== 'ALL') params.set('eventType', filter);
      const res = await api.get(`/admin/settings/audit/logs?${params}`);
      if (res.data?.success) {
        setLogs(res.data.data);
        setTotalPages(res.data.pagination?.totalPages || 1);
      }
    } catch (err) {
      console.error('Failed to load audit logs:', err);
    } finally {
      setLogsLoading(false);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 py-6">
        <div className="h-8 bg-slate-200 rounded w-1/3 animate-pulse" />
        <div className="grid grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => <div key={i} className="h-28 bg-slate-100 rounded-2xl animate-pulse" />)}
        </div>
        <div className="h-64 bg-slate-100 rounded-2xl animate-pulse" />
      </div>
    );
  }

  const statCards = [
    { icon: FileText, label: 'Total Audit Logs', value: stats?.totalLogs || 0, color: 'bg-blue-50 text-blue-600' },
    { icon: Clock, label: 'Last 24 Hours', value: stats?.last24hCount || 0, color: 'bg-teal-50 text-teal-600' },
    { icon: AlertTriangle, label: 'Failed Logins (30d)', value: stats?.failedLogins || 0, color: 'bg-red-50 text-red-600' },
    { icon: Eye, label: 'PHI Accesses (30d)', value: stats?.phiAccesses || 0, color: 'bg-purple-50 text-purple-600' },
  ];

  const complianceChecks = stats?.complianceChecks || [];

  return (
    <div className="space-y-6 py-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-headline text-2xl font-extrabold text-slate-900 tracking-tight">Compliance & Audit</h2>
          <p className="text-slate-500 text-sm mt-1">HIPAA compliance settings and audit trail</p>
        </div>
        <button onClick={() => { loadStats(); loadLogs(); }} className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-bold text-slate-600 hover:bg-slate-50 transition-all">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((card, i) => {
          const Icon = card.icon;
          return (
            <div key={i} className="bg-white rounded-2xl p-5 shadow-sm border border-slate-200">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${card.color}`}>
                <Icon className="w-5 h-5" />
              </div>
              <p className="text-2xl font-extrabold text-slate-900">{card.value.toLocaleString()}</p>
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mt-1">{card.label}</p>
            </div>
          );
        })}
      </div>

      {/* Compliance Status — driven by backend complianceChecks */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
        <h3 className="font-bold text-lg text-slate-900 mb-4 flex items-center gap-2">
          <Shield className="w-5 h-5 text-primary" /> HIPAA Compliance Status
        </h3>
        {complianceChecks.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-4">No compliance data available yet.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {complianceChecks.map((check, i) => (
              <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                <div className="min-w-0 flex-1">
                  <span className="text-sm font-medium text-slate-700">{check.label}</span>
                  <p className="text-xs text-slate-500 truncate">{check.detail}</p>
                </div>
                <span className={`text-xs font-bold px-2 py-1 rounded-full shrink-0 ml-2 ${check.status ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                  {check.status ? 'Compliant' : 'Review Needed'}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Audit Log Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="p-5 border-b border-slate-100 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <h3 className="font-bold text-lg text-slate-900">Audit Trail</h3>
          <div className="flex gap-2 flex-wrap">
            {['ALL', 'AUTH_LOGIN', 'AUTH_FAILED', 'PHI_ACCESS', 'SYSTEM_CONFIG_CHANGE', 'EXPORT'].map(t => (
              <button
                key={t}
                onClick={() => { setFilter(t); setPage(1); }}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                  filter === t ? 'bg-primary text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                }`}
              >
                {t === 'ALL' ? 'All' : eventTypeLabels[t] || t}
              </button>
            ))}
          </div>
        </div>

        {logsLoading ? (
          <div className="p-8 text-center text-slate-500">Loading audit logs...</div>
        ) : logs.length === 0 ? (
          <div className="p-12 text-center">
            <FileText className="w-10 h-10 mx-auto mb-3 text-slate-300" />
            <p className="font-bold text-slate-900">No audit logs found</p>
            <p className="text-sm text-slate-500 mt-1">Logs will appear as system activity occurs.</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {logs.map((log, i) => (
              <div key={log._id || i} className="px-5 py-3 flex items-center gap-4 hover:bg-slate-50 transition-colors">
                <span className={`px-2 py-1 rounded-lg text-xs font-bold shrink-0 ${eventTypeColors[log.eventType] || 'bg-slate-100 text-slate-600'}`}>
                  {eventTypeLabels[log.eventType] || log.eventType}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900 truncate">
                    {log.userName || 'System'} {log.action ? `— ${log.action}` : ''} {log.resourceType ? `on ${log.resourceType}` : ''}
                  </p>
                  <p className="text-xs text-slate-500">{log.ipAddress || ''} {log.details?.endpoint || ''}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-xs font-medium text-slate-600">{new Date(log.timestamp).toLocaleDateString()}</p>
                  <p className="text-xs text-slate-500">{new Date(log.timestamp).toLocaleTimeString()}</p>
                </div>
                {!log.success && <span className="text-xs font-bold text-red-600 bg-red-50 px-2 py-1 rounded-full">Failed</span>}
              </div>
            ))}
          </div>
        )}

        {totalPages > 1 && (
          <div className="p-4 border-t border-slate-100 flex items-center justify-center gap-2">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="px-3 py-1.5 rounded-lg text-xs font-bold bg-slate-100 text-slate-600 disabled:opacity-40">Previous</button>
            <span className="text-xs text-slate-500">Page {page} of {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="px-3 py-1.5 rounded-lg text-xs font-bold bg-slate-100 text-slate-600 disabled:opacity-40">Next</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ComplianceAudit;
